import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  IconButton,
  Button,
  Paper,
  TextField,
  Typography,
  useTheme,
  Divider,
} from "@mui/material";
import { components } from "../../types/api";
import CampaignDetailsCard from "../card/CampaignDetailsCard";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import {
  formatCurrency,
  formatNumber,
  formatNumberOrUnknown,
  formatPercentage,
  formatPercentageNoMultiply,
  formatDecimal,
} from "../../utils/formatUtils";
import { useTranslation } from "react-i18next";
import MetricsTimelineChart, {
  MetricDataPoint,
} from "../card/MetricsTimelineChart";
import ClearIcon from "@mui/icons-material/Clear";
import { exportMetricsToCSV } from "../../utils/exportUtils";
import {
  useGetMergedCampaignMetricsQuery,
  useGetMergedInsertionOrderMetricsQuery,
  useGetMergedLineItemMetricsQuery,
} from "../../store/apis/internalCampaignsMergeApi";
import { useDispatch, useSelector } from "react-redux";
import { setToastMessage } from "../../store/slices/app/appSlice";
import { selectUserDb } from "../../store/slices/session/sessionSelector";
import InsertionOrdersTable from "../table/InsertionOrdersTable";
import LineItemsTable from "../table/LineItemsTable";
import InsertionOrderMetricsChart from "../chart/InsertionOrderMetricsChart";
import LineItemMetricsChart from "../chart/LineItemMetricsChart";

const getNumberField = (source: unknown, key: string): number => {
  if (source && typeof source === "object") {
    const value = (source as Record<string, unknown>)[key];
    if (typeof value === "number" && Number.isFinite(value)) {
      return value;
    }
  }
  return 0;
};

const calculateRate = (
  numerator?: number | null,
  denominator?: number | null
): number => {
  const safeNumerator =
    typeof numerator === "number" && Number.isFinite(numerator) ? numerator : 0;
  const safeDenominator =
    typeof denominator === "number" && Number.isFinite(denominator)
      ? denominator
      : 0;

  if (safeDenominator <= 0) {
    return 0;
  }

  return safeNumerator / safeDenominator;
};

interface SpotifyMergeCampaignDetailsProps {
  isLoadingMergedCampaign: boolean;
  mergedCampaignId: string | undefined;
  mergedCampaignData:
    | components["schemas"]["MergedCampaignMetrics"]
    | null
    | undefined;
  metricsStartDate?: string;
  metricsEndDate?: string;
  onMetricsStartDateChange?: (value: string) => void;
  onMetricsEndDateChange?: (value: string) => void;
  onClearMetricsDateFilters?: () => void;
}

const SpotifyMergeCampaignDetails: React.FC<
  SpotifyMergeCampaignDetailsProps
> = ({
  isLoadingMergedCampaign,
  mergedCampaignData,
  mergedCampaignId,
  metricsStartDate,
  metricsEndDate,
  onMetricsStartDateChange,
  onMetricsEndDateChange,
  onClearMetricsDateFilters,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const userDb = useSelector(selectUserDb);
  const isAdmin = userDb?.role === "ADMIN";

  const format =
    (mergedCampaignData?.format as
      | "AUDIO"
      | "VIDEO"
      | "DISPLAY"
      | "ALL"
      | undefined) ?? "AUDIO";
  const isAudioFormat = format === "AUDIO";
  const isVideoFormat = format === "VIDEO";

  const [internalStartDate, setInternalStartDate] = useState<string>("");
  const [internalEndDate, setInternalEndDate] = useState<string>("");
  const [lineItemStartDate, setLineItemStartDate] = useState<string>("");
  const [lineItemEndDate, setLineItemEndDate] = useState<string>("");
  const [insertionOrderStartDate, setInsertionOrderStartDate] =
    useState<string>("");
  const [insertionOrderEndDate, setInsertionOrderEndDate] =
    useState<string>("");
  const [selectedLineItem, setSelectedLineItem] = useState<{
    id?: string;
    name?: string;
    dailyData: components["schemas"]["DailyLineItemMetrics"][];
    summary: components["schemas"]["MetricsLineItemSummary"];
  } | null>(null);
  const [selectedInsertionOrder, setSelectedInsertionOrder] = useState<{
    id?: string;
    name?: string;
    dailyData: components["schemas"]["DailyLineItemMetrics"][];
    summary: components["schemas"]["MetricsLineItemSummary"];
  } | null>(null);

  const formatDateForInput = useCallback((dateString?: string): string => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toISOString().split("T")[0];
  }, []);

  const campaignStartDate = formatDateForInput(mergedCampaignData?.startDate);
  const campaignEndDate = formatDateForInput(mergedCampaignData?.endDate);

  const resolvedStartDate = metricsStartDate ?? internalStartDate;
  const resolvedEndDate = metricsEndDate ?? internalEndDate;

  const handleMetricsStartDateChange = useCallback(
    (value: string) => {
      if (onMetricsStartDateChange) {
        onMetricsStartDateChange(value);
      } else {
        setInternalStartDate(value);
      }

      if (!value) {
        if (onMetricsEndDateChange) {
          onMetricsEndDateChange("");
        } else {
          setInternalEndDate("");
        }
      }
    },
    [onMetricsStartDateChange, onMetricsEndDateChange]
  );

  const handleMetricsEndDateChange = useCallback(
    (value: string) => {
      if (onMetricsEndDateChange) {
        onMetricsEndDateChange(value);
      } else {
        setInternalEndDate(value);
      }
    },
    [onMetricsEndDateChange]
  );

  const handleClearMetricsDates = useCallback(() => {
    if (onClearMetricsDateFilters) {
      onClearMetricsDateFilters();
    } else {
      setInternalStartDate("");
      setInternalEndDate("");
    }
  }, [onClearMetricsDateFilters]);

  const {
    data: filteredMergedCampaignData,
    isLoading: isLoadingFilteredMerged,
  } = useGetMergedCampaignMetricsQuery(
    {
      mergedCampaignId: mergedCampaignId || "",
      ...(resolvedStartDate && { startDate: resolvedStartDate }),
      ...(resolvedEndDate && { endDate: resolvedEndDate }),
    },
    {
      skip: !mergedCampaignId || (!resolvedStartDate && !resolvedEndDate),
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    }
  );

  const {
    data: lineItemMetrics,
    isLoading: isLoadingLineItemMetrics,
    error: lineItemMetricsError,
  } = useGetMergedLineItemMetricsQuery(
    {
      mergedCampaignId: mergedCampaignId || "",
      startDate: lineItemStartDate,
      endDate: lineItemEndDate,
    },
    {
      skip: !mergedCampaignId,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    }
  );

  const {
    data: insertionOrderMetrics,
    isLoading: isLoadingInsertionOrderMetrics,
    error: insertionOrderMetricsError,
  } = useGetMergedInsertionOrderMetricsQuery(
    {
      mergedCampaignId: mergedCampaignId || "",
      startDate: insertionOrderStartDate,
      endDate: insertionOrderEndDate,
    },
    {
      skip: !mergedCampaignId,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    }
  );

  const isFilterActive = Boolean(resolvedStartDate || resolvedEndDate);

  const displayStartDate = resolvedStartDate || campaignStartDate;
  const displayEndDate = resolvedEndDate || campaignEndDate;

  const effectiveMergedCampaignData =
    isFilterActive && filteredMergedCampaignData
      ? filteredMergedCampaignData
      : mergedCampaignData;

  const timelineData: MetricDataPoint[] = useMemo(() => {
    const daily = effectiveMergedCampaignData?.metrics?.dailyData || [];

    return daily.map((day) => ({
      date: day.date,
      ctr: calculateRate(day.clicks, day.impressions),
      cpc: day.cpc ?? 0,
      cpm: day.cpm ?? 0,
      impressions: day.impressions ?? 0,
      clicks: day.clicks ?? 0,
      cost: day.cost ?? 0,
      completeViews: day.completeViews ?? 0,
    }));
  }, [effectiveMergedCampaignData?.metrics?.dailyData]);

  const effectiveSummary = effectiveMergedCampaignData?.metrics?.summary;
  const combinedLoading = isLoadingMergedCampaign || isLoadingFilteredMerged;
  const summaryCtr = useMemo(() => {
    if (!effectiveSummary) {
      return 0;
    }

    return calculateRate(
      effectiveSummary.totalClicks,
      effectiveSummary.totalImpressions
    );
  }, [effectiveSummary]);

  useEffect(() => {
    if (mergedCampaignData?.startDate) {
      const formattedStart = formatDateForInput(mergedCampaignData.startDate);
      if (!lineItemStartDate) setLineItemStartDate(formattedStart);
      if (!insertionOrderStartDate) setInsertionOrderStartDate(formattedStart);
      if (!resolvedStartDate) {
        handleMetricsStartDateChange(formattedStart);
      }
    }
    if (mergedCampaignData?.endDate) {
      const formattedEnd = formatDateForInput(mergedCampaignData.endDate);
      if (!lineItemEndDate) setLineItemEndDate(formattedEnd);
      if (!insertionOrderEndDate) setInsertionOrderEndDate(formattedEnd);
      if (!resolvedEndDate) {
        handleMetricsEndDateChange(formattedEnd);
      }
    }
  }, [
    mergedCampaignData,
    formatDateForInput,
    lineItemStartDate,
    lineItemEndDate,
    insertionOrderStartDate,
    insertionOrderEndDate,
    resolvedStartDate,
    resolvedEndDate,
    handleMetricsStartDateChange,
    handleMetricsEndDateChange,
  ]);

  useEffect(() => {
    if (
      lineItemMetrics?.lineItems &&
      lineItemMetrics.lineItems.length > 0 &&
      !selectedLineItem
    ) {
      setSelectedLineItem(lineItemMetrics.lineItems[0]);
    }
  }, [lineItemMetrics, selectedLineItem]);

  useEffect(() => {
    if (
      insertionOrderMetrics?.insertionOrders &&
      insertionOrderMetrics.insertionOrders.length > 0 &&
      !selectedInsertionOrder
    ) {
      setSelectedInsertionOrder(insertionOrderMetrics.insertionOrders[0]);
    }
  }, [insertionOrderMetrics, selectedInsertionOrder]);

  useEffect(() => {
    if (lineItemMetricsError || insertionOrderMetricsError) {
      dispatch(
        setToastMessage({
          message: t("common.errorFetchingData", {
            defaultValue: "Error fetching data",
          }),
          severity: "error",
        })
      );
    }
  }, [lineItemMetricsError, insertionOrderMetricsError, dispatch, t]);

  const handleInsertionOrderClick = useCallback(
    (insertionOrder: {
      id?: string;
      name?: string;
      dailyData: components["schemas"]["DailyLineItemMetrics"][];
      summary: components["schemas"]["MetricsLineItemSummary"];
    }) => {
      setSelectedInsertionOrder(insertionOrder);
    },
    []
  );

  const handleLineItemClick = useCallback(
    (lineItem: {
      id?: string;
      name?: string;
      dailyData: components["schemas"]["DailyLineItemMetrics"][];
      summary: components["schemas"]["MetricsLineItemSummary"];
    }) => {
      setSelectedLineItem(lineItem);
    },
    []
  );

  const getIndexListenComplete = () => {
    return calculateRate(
      effectiveSummary?.totalCompleteViews,
      effectiveSummary?.totalImpressions
    );
  };

  const handleExportMetrics = () => {
    const dailyData = effectiveMergedCampaignData?.metrics?.dailyData || [];

    const formattedData = dailyData.map((day) => ({
      date: day.date,
      impressions: day.impressions || 0,
      clicks: day.clicks || 0,
      ctr: calculateRate(day.clicks, day.impressions),
      cpm: day.cpm || 0,
      cpc: day.cpc || 0,
      cost: day.cost || 0,
      completeViews: day.completeViews || 0,
      cpv: getNumberField(day, "cpv"),
      vtr: getNumberField(day, "vtr"),
      dailyInvestment: day.cost || 0,
      dailyImpressions: day.impressions || 0,
      dailyCTR: calculateRate(day.clicks, day.impressions),
      conversions: day.conversions || 0,
      currency: effectiveMergedCampaignData?.currency_enum || "EUR",
    }));

    exportMetricsToCSV(
      formattedData,
      effectiveMergedCampaignData?.name || "export",
      t,
      dispatch
    );
  };

  const handleExportInsertionOrderMetrics = useCallback(() => {
    if (!insertionOrderMetrics?.insertionOrders) {
      dispatch(
        setToastMessage({
          message: t("common.noDataToExport", {
            defaultValue: "No data to export",
          }),
          severity: "warning",
        })
      );
      return;
    }

    const isAudioFormat = format === "AUDIO";

    const headers = [
      t("campaigns.name", { defaultValue: "Name" }),
      t("campaigns.investment", { defaultValue: "Investment" }),
      t("campaigns.impressions", { defaultValue: "Impressions" }),
      t("campaigns.clicks", { defaultValue: "Clicks" }),
      t("campaigns.ctr", { defaultValue: "CTR" }),
      t("campaigns.cpm", { defaultValue: "CPM" }),
      t("campaigns.cpc", { defaultValue: "CPC" }),
      t("campaigns.cpv", { defaultValue: "CPV" }),
      isAudioFormat
        ? t("campaigns.indexListenComplete", {
            defaultValue: "Index Listen Complete",
          })
        : t("campaigns.vtr", { defaultValue: "VTR" }),
      t("campaigns.listenComplete", {
        defaultValue: "Listen Complete",
      }),
      t("campaigns.totalReach", { defaultValue: "Reach" }),
    ];

    if (isAdmin) {
      headers.push(t("campaigns.realCost", { defaultValue: "Real Cost" }));
    }

    const rows = insertionOrderMetrics.insertionOrders.map((order) => {
      const { summary } = order;
      const ctrValue = calculateRate(
        summary.totalClicks,
        summary.totalImpressions
      );
      const listenCompleteIndex = calculateRate(
        summary.totalCompleteViews,
        summary.totalImpressions
      );
      const row = [
        order.name || "Unnamed AdOrder",
        formatCurrency(summary.totalCost, mergedCampaignData?.currency_enum),
        summary.totalImpressions?.toString() || "0",
        summary.totalClicks?.toString() || "0",
        formatPercentage(ctrValue),
        formatCurrency(summary.avgCPM, mergedCampaignData?.currency_enum),
        formatCurrency(summary.avgCPC, mergedCampaignData?.currency_enum),
        formatCurrency(
          getNumberField(summary, "avgCPV"),
          mergedCampaignData?.currency_enum
        ),
        isAudioFormat
          ? formatPercentage(listenCompleteIndex)
          : formatPercentageNoMultiply(getNumberField(summary, "avgVTR")),
        formatNumber(summary.totalCompleteViews || 0),
        formatNumberOrUnknown(summary.totalUniqueUsers, ""),
      ];

      if (isAdmin && summary.totalRealCost !== undefined) {
        row.push(
          formatCurrency(
            summary.totalRealCost,
            mergedCampaignData?.currency_enum
          )
        );
      }

      return row;
    });

    const BOM = new Uint8Array([0xef, 0xbb, 0xbf]);
    const csvContent = [headers, ...rows].map((r) => r.join(",")).join("\n");
    const blob = new Blob([BOM, csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `spotify-insertion-orders-${mergedCampaignId}-${
      new Date().toISOString().split("T")[0]
    }.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [
    insertionOrderMetrics,
    dispatch,
    t,
    isAdmin,
    mergedCampaignData?.currency_enum,
    mergedCampaignId,
    format,
  ]);

  const handleExportLineItemMetrics = useCallback(() => {
    if (!lineItemMetrics?.lineItems) {
      dispatch(
        setToastMessage({
          message: t("common.noDataToExport", {
            defaultValue: "No data to export",
          }),
          severity: "warning",
        })
      );
      return;
    }

    try {
      const flattenedData = lineItemMetrics.lineItems.flatMap((lineItem) =>
        lineItem.dailyData.map((daily) => {
          const ctr = calculateRate(daily.clicks, daily.impressions);
          const listenCompleteIndex = calculateRate(
            daily.completeViews,
            daily.impressions
          );
          const baseData = {
            date: daily.date,
            lineItemName: lineItem.name || "Unnamed AdLine",
            lineItemId: lineItem.id || "",
            impressions: daily.impressions ?? 0,
            clicks: daily.clicks ?? 0,
            ctr,
            cpm: daily.cpm ?? 0,
            cpc: daily.cpc ?? 0,
            cost: daily.cost ?? 0,
            cpv: getNumberField(daily, "cpv"),
            vtr: getNumberField(daily, "vtr"),
            completeViews: daily.completeViews ?? 0,
            listenCompleteIndex,
          };
          return isAdmin
            ? { ...baseData, realCost: daily.realCost ?? 0 }
            : baseData;
        })
      );

      const isAudioFormat = format === "AUDIO";

      const headers = [
        "Date",
        "AdLine Name",
        "AdLine ID",
        "Impressions",
        "Clicks",
        "CTR",
        "CPM",
        "CPC",
        "Cost",
        "CPV",
        isAudioFormat ? "Index Listen Complete" : "VTR",
        "Complete Views",
      ];

      if (isAdmin) {
        headers.push("Real Cost");
      }

      const rows = flattenedData.map((item) => {
        const values = [
          item.date,
          item.lineItemName,
          item.lineItemId,
          item.impressions.toString(),
          item.clicks.toString(),
          formatPercentage(item.ctr),
          item.cpm.toString(),
          item.cpc.toString(),
          item.cost.toString(),
          item.cpv.toString(),
          isAudioFormat
            ? formatPercentage(item.listenCompleteIndex)
            : formatPercentageNoMultiply(item.vtr),
          item.completeViews.toString(),
        ];

        if (isAdmin && "realCost" in item) {
          values.push(item.realCost.toString());
        }

        return values;
      });

      const BOM = new Uint8Array([0xef, 0xbb, 0xbf]);
      const csvContent = [
        headers.join(","),
        ...rows.map((row) => row.join(",")),
      ].join("\n");
      const blob = new Blob([BOM, csvContent], {
        type: "text/csv;charset=utf-8;",
      });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.href = url;
      link.download = `spotify-line-items-${mergedCampaignId}-${
        new Date().toISOString().split("T")[0]
      }.csv`;
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      dispatch(
        setToastMessage({
          message: t("common.exportSuccess", {
            defaultValue: "AdLine data exported successfully",
          }),
          severity: "success",
        })
      );
    } catch (error) {
      console.error("Error exporting Spotify line item metrics:", error);
      dispatch(
        setToastMessage({
          message: t("campaigns.exportError", {
            defaultValue: "Error exporting AdLine metrics data",
          }),
          severity: "error",
        })
      );
    }
  }, [lineItemMetrics, dispatch, t, isAdmin, mergedCampaignId, format]);

  if (combinedLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!effectiveSummary) {
    return (
      <Box
        sx={{
          p: 3,
          borderRadius: 2,
          border: `1px solid ${theme.palette.grey[200]}`,
          bgcolor: theme.palette.background.paper,
        }}
      >
        <Typography variant="body1" color="text.secondary">
          No metrics available for this campaign.
        </Typography>
      </Box>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 2,
        border: `1px solid ${theme.palette.grey[200]}`,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 2, sm: 0 },
          mb: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {t("campaigns.metrics", { defaultValue: "Metrics" })}
        </Typography>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            rowGap: 2,
            flexWrap: "wrap",
          }}
        >
          <TextField
            label={t("common.startDate", { defaultValue: "Start Date" })}
            type="date"
            size="small"
            value={displayStartDate || ""}
            onChange={(e) => handleMetricsStartDateChange(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{
              min: campaignStartDate,
              max: displayEndDate || campaignEndDate,
            }}
            sx={{ width: 150 }}
          />

          <TextField
            label={t("common.endDate", { defaultValue: "End Date" })}
            type="date"
            size="small"
            value={displayEndDate || ""}
            onChange={(e) => handleMetricsEndDateChange(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{
              min: displayStartDate || campaignStartDate,
              max: campaignEndDate,
            }}
            sx={{ width: 150 }}
            disabled={!displayStartDate}
          />

          <IconButton
            onClick={handleClearMetricsDates}
            size="small"
            color="primary"
            title={t("common.clearFilters", {
              defaultValue: "Clear Filters",
            })}
          >
            <ClearIcon />
          </IconButton>

          <Button
            variant="outlined"
            color="primary"
            size="small"
            startIcon={<FileDownloadIcon />}
            onClick={handleExportMetrics}
            sx={{
              mt: { xs: 1, sm: 0 },
              ml: { xs: 0, sm: 1 },
              height: 36,
              textTransform: "none",
              fontWeight: 500,
            }}
          >
            {t("campaigns.exportMetrics", { defaultValue: "Export Data" })}
          </Button>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 3,
          "& > *": {
            flex: "1 1 calc(16.66% - 20px)",
            minWidth: "150px",
          },
        }}
      >
        <CampaignDetailsCard
          value={formatCurrency(
            effectiveSummary.totalCost,
            mergedCampaignData?.currency_enum
          )}
          label={t("campaigns.cost", { defaultValue: "Cost" })}
        />
        <CampaignDetailsCard
          value={formatNumber(effectiveSummary.totalImpressions)}
          label={t("campaigns.impressions", { defaultValue: "Impressions" })}
        />
        <CampaignDetailsCard
          value={formatNumber(effectiveSummary.totalClicks)}
          label={t("campaigns.clicks", { defaultValue: "Clicks" })}
        />
        <CampaignDetailsCard
          value={formatCurrency(
            effectiveSummary.avgCPM,
            mergedCampaignData?.currency_enum
          )}
          label={t("campaigns.cpm", { defaultValue: "CPM" })}
        />
        <CampaignDetailsCard
          value={formatCurrency(
            effectiveSummary.avgCPC,
            mergedCampaignData?.currency_enum
          )}
          label={t("campaigns.cpc", { defaultValue: "CPC" })}
        />
        {isVideoFormat && (
          <CampaignDetailsCard
            value={formatNumber(effectiveSummary.visualizations || 0)}
            label={t("campaigns.visualizations", {
              defaultValue: "Video Visualizations",
            })}
          />
        )}
        <CampaignDetailsCard
          value={formatNumber(effectiveSummary.uniqueUsers || 0)}
          label={t("campaigns.totalReach", { defaultValue: "Reach" })}
        />
        <CampaignDetailsCard
          value={formatDecimal(effectiveSummary.frequency || 0)}
          label={t("campaigns.frequency", { defaultValue: "Frequency" })}
        />
        <CampaignDetailsCard
          value={formatPercentage(summaryCtr)}
          label={t("campaigns.ctr", { defaultValue: "CTR" })}
        />
        <CampaignDetailsCard
          value={formatNumber(effectiveSummary.totalCompleteViews || 0)}
          label={t("campaigns.listenComplete", {
            defaultValue: "Listen Complete",
          })}
        />
        {isAudioFormat && (
          <CampaignDetailsCard
            value={formatPercentage(getIndexListenComplete())}
            label={t("campaigns.indexListenComplete", {
              defaultValue: "Index Listen Complete",
            })}
          />
        )}
        {isVideoFormat && (
          <CampaignDetailsCard
            value={formatPercentageNoMultiply(effectiveSummary.avgVTR)}
            label={t("campaigns.vtr", { defaultValue: "VTR" })}
          />
        )}
      </Box>

      <Box sx={{ mt: 4 }}>
        <MetricsTimelineChart
          title={t("campaigns.performanceOverTime", {
            defaultValue: "Performance Over Time",
          })}
          subtitle={t("campaigns.performanceOverTimeSubtitle", {
            defaultValue: "Track key metrics across the selected date range",
          })}
          data={timelineData}
          isLoading={combinedLoading}
        />
      </Box>

      <Divider sx={{ my: 4 }} />

      <Paper
        sx={{
          p: 3,
          borderRadius: 2,
          backgroundColor: theme.palette.background.paper,
          mb: 3,
        }}
        elevation={0}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Typography variant="h6" fontWeight={600} color="text.primary">
            {t("campaigns.insertionOrders", {
              defaultValue: "AdOrder",
            })}
          </Typography>

          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <TextField
              label={t("campaigns.startDate", { defaultValue: "Start Date" })}
              type="date"
              size="small"
              value={insertionOrderStartDate}
              onChange={(e) => setInsertionOrderStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 150 }}
            />

            <TextField
              label={t("campaigns.endDate", { defaultValue: "End Date" })}
              type="date"
              size="small"
              value={insertionOrderEndDate}
              onChange={(e) => setInsertionOrderEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 150 }}
              disabled={!insertionOrderStartDate}
            />

            <IconButton
              onClick={() => {
                setInsertionOrderStartDate("");
                setInsertionOrderEndDate("");
              }}
              size="small"
              color="primary"
              title={t("common.clearFilters", {
                defaultValue: "Clear Filters",
              })}
            >
              <ClearIcon />
            </IconButton>

            <Button
              variant="outlined"
              color="primary"
              size="small"
              startIcon={<FileDownloadIcon />}
              onClick={handleExportInsertionOrderMetrics}
              sx={{
                height: 36,
                textTransform: "none",
                fontWeight: 500,
              }}
            >
              {t("campaigns.exportInsertionOrders", {
                defaultValue: "Export",
              })}
            </Button>
          </Box>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {isLoadingInsertionOrderMetrics ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : insertionOrderMetrics?.insertionOrders &&
          insertionOrderMetrics.insertionOrders.length > 0 ? (
          <>
            <InsertionOrdersTable
              insertionOrders={insertionOrderMetrics.insertionOrders}
              isLoading={isLoadingInsertionOrderMetrics}
              currency={mergedCampaignData?.currency_enum || "EUR"}
              onInsertionOrderClick={handleInsertionOrderClick}
              selectedInsertionOrderId={selectedInsertionOrder?.id}
              format={format}
              useTotalsForCtr
            />
            {selectedInsertionOrder && (
              <Box sx={{ mt: 3 }}>
                <InsertionOrderMetricsChart
                  dailyData={selectedInsertionOrder.dailyData}
                  isLoading={isLoadingInsertionOrderMetrics}
                  title={selectedInsertionOrder.name || "Unnamed AdOrder"}
                  currency={mergedCampaignData?.currency_enum || "EUR"}
                  isAdmin={isAdmin}
                />
              </Box>
            )}
          </>
        ) : (
          <Typography variant="body1" sx={{ py: 2 }}>
            {t("common.noData", {
              defaultValue: "No insertion orders data available",
            })}
          </Typography>
        )}
      </Paper>

      <Paper
        sx={{
          p: 3,
          borderRadius: 2,
          backgroundColor: theme.palette.background.paper,
        }}
        elevation={0}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Typography variant="h6" fontWeight={600} color="text.primary">
            {t("campaigns.lineItems", {
              defaultValue: "AdLine",
            })}
          </Typography>

          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <TextField
              label={t("common.startDate", { defaultValue: "Start Date" })}
              type="date"
              size="small"
              value={lineItemStartDate}
              onChange={(e) => setLineItemStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 150 }}
            />

            <TextField
              label={t("common.endDate", { defaultValue: "End Date" })}
              type="date"
              size="small"
              value={lineItemEndDate}
              onChange={(e) => setLineItemEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 150 }}
              disabled={!lineItemStartDate}
            />

            <IconButton
              onClick={() => {
                setLineItemStartDate("");
                setLineItemEndDate("");
              }}
              size="small"
              color="primary"
              title={t("common.clearFilters", {
                defaultValue: "Clear Filters",
              })}
            >
              <ClearIcon />
            </IconButton>

            <Button
              variant="outlined"
              color="primary"
              size="small"
              startIcon={<FileDownloadIcon />}
              onClick={handleExportLineItemMetrics}
              sx={{
                height: 36,
                textTransform: "none",
                fontWeight: 500,
              }}
            >
              {t("campaigns.exportLineItems", {
                defaultValue: "Export",
              })}
            </Button>
          </Box>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {isLoadingLineItemMetrics ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : lineItemMetrics?.lineItems &&
          lineItemMetrics.lineItems.length > 0 ? (
          <>
            <LineItemsTable
              lineItems={lineItemMetrics.lineItems}
              isLoading={isLoadingLineItemMetrics}
              onLineItemClick={handleLineItemClick}
              selectedLineItemId={selectedLineItem?.id}
              currency={mergedCampaignData?.currency_enum || "EUR"}
              format={format}
              useTotalsForCtr
            />
            {selectedLineItem && (
              <Box sx={{ mt: 3 }}>
                <LineItemMetricsChart
                  dailyData={selectedLineItem.dailyData}
                  isLoading={isLoadingLineItemMetrics}
                  title={selectedLineItem.name || "Unnamed AdLine"}
                  currency={mergedCampaignData?.currency_enum || "EUR"}
                  isAdmin={isAdmin}
                />
              </Box>
            )}
          </>
        ) : (
          <Typography variant="body1" sx={{ py: 2 }}>
            {t("common.noData", {
              defaultValue: "No line items data available",
            })}
          </Typography>
        )}
      </Paper>
    </Paper>
  );
};

export default SpotifyMergeCampaignDetails;
